import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import moment from 'moment';
import { AuthorizationService } from '@pure/authz-authorizer';
import { take } from 'rxjs/operators';
import {
    Incident,
    getLicenseTypeDisplayInfo,
    getRecommendationDisplayName,
    isAdaptiveLicenseType,
    NvmeNowEolComponent,
    RecommendationCode,
    WORKFLOW_RECIPE_CARD_TARGET_NAME,
    RelatedRecommendations,
    groupRecommendations,
    SubscriptionType,
} from '@pure1/data';
import { formatNetworkSpeedSI } from '../../../utils/formatNetworkSpeed';
import { formatIOPsStr } from '../../../utils/formatIOPs';
import {
    ComparisonRow,
    ContractRenewalRenderInfo,
    ContractRenewalTableRow,
    createEoscRenewal,
    EoneCapacityExpansionRenderInfo,
    EoscFbRenderInfo,
    EoscRenewal,
    EoneTierUpgradeRenderInfo,
    FbNorwayEolRenderInfo,
    NvmeNowDetailRow,
    NvmeNowRenderInfo,
    RecommendationRenderInfo,
    WorkflowRecipeRenderInfo,
} from 'core/src/data/models/recommendations.models';
import { FormatFullInDaysPipe } from '../../../ui/pipes/format-full-in-days.pipe';
import {
    buildEoneCapacityExpansionChartOptions,
    buildEoneCapacityExpansionSparklineOptions,
    buildEoneUpgradeTierChartOptions,
} from '../utils/recommendations-chart-utils';
// Importing these constants also brings each plugin module into the dependency graph,
// which runs its `registerAsCardPlugin` / `registerAsDialogPlugin` side effect and
// populates the recommendation component registry.
import { CONTRACT_RENEWAL_CARD_PLUGIN_NAME } from '../recommendation-types/contract-renewal/contract-renewal-card-plugin.component';
import { CONTRACT_RENEWAL_DIALOG_PLUGIN_NAME } from '../recommendation-types/contract-renewal/contract-renewal-dialog-plugin.component';
import { EONE_TIER_UPGRADE_CARD_PLUGIN_NAME } from '../recommendation-types/eone-tier-upgrade/eone-tier-upgrade-card-plugin.component';
import { EONE_TIER_UPGRADE_DIALOG_PLUGIN_NAME } from '../recommendation-types/eone-tier-upgrade/eone-tier-upgrade-dialog-plugin.component';
import { EONE_CAPACITY_EXPANSION_CARD_PLUGIN_NAME } from '../recommendation-types/eone-capacity-expansion/eone-capacity-expansion-card-plugin.component';
import { EONE_CAPACITY_EXPANSION_DIALOG_PLUGIN_NAME } from '../recommendation-types/eone-capacity-expansion/eone-capacity-expansion-dialog-plugin.component';
import { NVME_NOW_CARD_PLUGIN_NAME } from '../recommendation-types/nvme-now/nvme-now-card-plugin.component';
import { NVME_NOW_DIALOG_PLUGIN_NAME } from '../recommendation-types/nvme-now/nvme-now-dialog-plugin.component';
import { WORKFLOW_RECIPE_CARD_PLUGIN_NAME } from '../recommendation-types/workflow-recipe/workflow-recipe-card-plugin.component';
import { WORKFLOW_RECIPE_DIALOG_PLUGIN_NAME } from '../recommendation-types/workflow-recipe/workflow-recipe-dialog-plugin.component';
import { FB_NORWAY_EOL_CARD_PLUGIN_NAME } from '../recommendation-types/fb-norway-eol/fb-norway-eol-card-plugin.component';
import { FB_NORWAY_EOL_DIALOG_PLUGIN_NAME } from '../recommendation-types/fb-norway-eol/fb-norway-eol-dialog-plugin.component';
import { EOSC_FB_CARD_PLUGIN_NAME } from '../recommendation-types/eosc-fb/eosc-fb-card-plugin.component';
import { EOSC_FB_DIALOG_PLUGIN_NAME } from '../recommendation-types/eosc-fb/eosc-fb-dialog-plugin.component';
import { buildEvercycleBuybackRenderInfo } from '../recommendation-types/evercycle-buyback/evercycle-buyback-render-info';
// The buyback builder takes its plugin keys from a leaf file, so nothing above loads the buyback
// plugin components. Importing them here runs their `registerAs*Plugin` side effect, without which
// the container finds no card or dialog for a buyback item.
import '../recommendation-types/evercycle-buyback/evercycle-buyback-card-plugin.component';
import '../recommendation-types/evercycle-buyback/evercycle-buyback-dialog-plugin.component';

/**
 * Service for processing recommendations and converting them to UI render information.
 *
 * This service handles all UI-specific logic for recommendations, keeping the data layer
 * (Incident model) free from UI dependencies and avoiding circular dependencies.
 */
@Injectable({
    providedIn: 'root',
})
export class RecommendationProcessingService {
    constructor(private readonly authorizationService: AuthorizationService) {}

    /**
     * Creates a signal that resolves asynchronously based on authorization check.
     * The signal starts with a default value (false) and updates when the auth check completes.
     *
     * @param permission - The permission string to check
     * @param orgId - The organization ID to check permission for
     * @returns WritableSignal that updates when auth check completes
     */
    private createAsyncPermissionSignal(permission: string, orgId: number): WritableSignal<boolean> {
        const permissionSignal = signal<boolean>(false);

        this.authorizationService
            .isAllowed(permission, {
                type: 'ORGANIZATION_PHONEBOOK_ID',
                id: orgId,
            })
            .pipe(take(1))
            .subscribe(allowed => {
                permissionSignal.set(allowed);
            });

        return permissionSignal;
    }

    /** Like createAsyncPermissionSignal, but checks the permission without an organization scope. */
    private createUnscopedPermissionSignal(permission: string): WritableSignal<boolean> {
        const permissionSignal = signal<boolean>(false);

        this.authorizationService
            .isAllowed(permission)
            .pipe(take(1))
            .subscribe(allowed => {
                permissionSignal.set(allowed);
            });

        return permissionSignal;
    }

    /**
     * Processes recommendations and returns only the renderable recommendations.
     * Each renderable recommendation represents one recommendation card to display (one per appliance).
     *
     * This method groups related recommendations (EOL, EoSC, ForeverNow) by appliance and selects
     * the leading recommendation for each group, then filters the input array to return only these
     * leading recommendations in their original order.
     *
     * @param recommendations - Array of all recommendations
     * @param isQuoteContractRenewalGuiEnabled - Optional flag to control whether quote-based contract renewal recommendations should be included (default: false)
     * @param isEvercycleBuybackEnabled - Optional flag to control whether EverCycle Buyback recommendations should be included (default: false)
     * @returns Object containing:
     *   - renderableRecommendations: Array of renderable leading recommendations (one per appliance) in the original order
     *   - leadingRecIdToRelatedRecsMap: Map of leading recommendation IDs to their related recommendations
     *   - recIdToRenderInfoMap: Map of recommendation IDs to their RenderInfo objects (for recommendations that support toRenderInfo)
     */
    processRecommendations(
        recommendations: Incident[] | null,
        isQuoteContractRenewalGuiEnabled = false,
        isEvercycleBuybackEnabled = false,
    ): {
        renderableRecommendations: Incident[];
        leadingRecIdToRelatedRecsMap: Map<string, RelatedRecommendations>;
        recIdToRenderInfoMap: Map<string, any>;
    } {
        if (!recommendations || recommendations.length === 0) {
            return {
                renderableRecommendations: [],
                leadingRecIdToRelatedRecsMap: new Map(),
                recIdToRenderInfoMap: new Map(),
            };
        }

        // Pre-compute RenderInfo for recommendations that support it, and use it for filtering
        const recIdToRenderInfoMap = new Map<string, any>();

        // Filter out recommendations that are not renderable
        // For recommendations that support toRenderInfo, we compute it once here
        recommendations = recommendations.filter(incident => {
            return this.isRenderableRecommendation(
                incident,
                recIdToRenderInfoMap,
                isQuoteContractRenewalGuiEnabled,
                isEvercycleBuybackEnabled,
            );
        });

        // Group recommendations by appliance (leading recommendation)
        const leadingRecIdToRelatedRecsMap = groupRecommendations(recommendations);

        // To display recommendation cards, we need a list of leading recommendations
        // as starting points for the cards, in the order received from incident-service's response.
        // Therefore, we rely on the ordering of the response, and filter for only leading recommendations.
        const renderableRecommendations = recommendations.filter(rec => leadingRecIdToRelatedRecsMap.has(rec.id));

        return {
            renderableRecommendations,
            leadingRecIdToRelatedRecsMap,
            recIdToRenderInfoMap,
        };
    }

    /**
     * Converts an incident to a RenderInfo object for display in the UI.
     * Returns the appropriate RenderInfo type based on the recommendation code.
     * Throws an error if the recommendation cannot be rendered.
     */
    private convertIncidentToRenderInfo(incident: Incident): RecommendationRenderInfo {
        switch (incident.recommendationCode) {
            case RecommendationCode.QuoteBasedContractRenewal:
                return this.createQuoteBasedContractRenewalRenderInfo(incident);
            case RecommendationCode.EoneLicenseUpgrade:
                return this.createEoneTierUpgradeRenderInfoFromIncident(incident);
            case RecommendationCode.EoneCapacityExpansion:
                return this.createEoneCapacityExpansionRenderInfoFromIncident(incident);
            case RecommendationCode.NvmeNow:
                return this.createNvmeNowRenderInfoFromIncident(incident);
            case RecommendationCode.WorkflowRecipe:
                return this.createWorkflowRecipeRenderInfoFromIncident(incident);
            case RecommendationCode.EvercycleBuybackFA:
            case RecommendationCode.EvercycleBuybackFB:
                return buildEvercycleBuybackRenderInfo(incident);
            default:
                throw new Error(`Recommendation type ${incident.recommendationCode} does not support rendering yet`);
        }
    }

    private isRenderableRecommendation(
        incident: Incident,
        recIdToRenderInfoMap: Map<string, any>,
        isQuoteContractRenewalGuiEnabled = false,
        isEvercycleBuybackEnabled = false,
    ): boolean {
        try {
            // For recommendations that support rendering, compute it once and store it
            if (incident.isQuoteBasedContractRenewalRecommendation()) {
                // Filter out quote-based contract renewal recommendations if feature flag is disabled
                if (!isQuoteContractRenewalGuiEnabled) {
                    return false;
                }
                recIdToRenderInfoMap.set(incident.id, this.convertIncidentToRenderInfo(incident));
                return true;
            }

            if (incident.isEvergreenOneRecommendation()) {
                recIdToRenderInfoMap.set(incident.id, this.convertIncidentToRenderInfo(incident));
                return true;
            }

            if (incident.isNvmeNowRecommendation()) {
                recIdToRenderInfoMap.set(incident.id, this.convertIncidentToRenderInfo(incident));
                return true;
            }

            if (incident.isWorkflowRecipeRecommendation()) {
                // A payload with no usable recipe has nothing to render, and the RenderInfo
                // constructor would throw on it, so check before converting.
                const details = incident.getWorkflowRecipeDetails();
                if (!details.validate()) {
                    // recommendation-service only emits this program with recipes attached
                    // (see WorkflowRecipePayload), so arriving here means the payload did not
                    // match what the card parses — most likely a field renamed on the wire.
                    // Dropping the card silently would leave that invisible: no error, just a
                    // recommendation nobody ever sees.
                    console.error(
                        'Workflow recipe recommendation carried no usable recipe — payload does not match the expected shape',
                        { incidentId: incident?.id, signal: details?.signal },
                    );
                    return false;
                }
                recIdToRenderInfoMap.set(incident.id, this.convertIncidentToRenderInfo(incident));
                return true;
            }

            if (incident.isFbNorwayEolRecommendation()) {
                recIdToRenderInfoMap.set(incident.id, this.createFbNorwayEolRenderInfo(incident));
                return true;
            }

            if (incident.recommendationCode === RecommendationCode.EoSCFB) {
                recIdToRenderInfoMap.set(incident.id, this.createEoscFbRenderInfo(incident));
                return true;
            }

            if (incident.isEvercycleBuybackRecommendation()) {
                // Hidden entirely while the flag is off: the item has no legacy card to fall back to.
                if (!isEvercycleBuybackEnabled) {
                    return false;
                }
                recIdToRenderInfoMap.set(incident.id, this.convertIncidentToRenderInfo(incident));
                return true;
            }

            // For other recommendations, use the validate method if available
            const recDetails = incident?.additionalInformation;
            if (recDetails && 'validate' in recDetails && typeof recDetails.validate === 'function') {
                return recDetails.validate();
            }
            return true;
        } catch (e) {
            console.error('Skipping recommendation due to invalid payload', incident?.id, e);
            return false;
        }
    }

    /**
     * Creates ContractRenewalRenderInfo from a QuoteBasedContractRenewal incident.
     * Throws an error if the incident cannot be converted.
     * Creates a signal for allowDismiss and triggers async auth check to update it.
     */
    private createQuoteBasedContractRenewalRenderInfo(incident: Incident): ContractRenewalRenderInfo {
        const details = incident.getQuoteBasedContractRenewalDetails();
        const earliestExpiringTarget = this.findEarliestExpiringTarget(details.supportContractTargets);
        const daysRemaining = this.calculateDaysRemaining(earliestExpiringTarget);
        const emphasis = daysRemaining <= 20 ? 'danger' : 'warning';

        // Create signals that resolve asynchronously based on permissions
        const allowDismiss = this.createAsyncPermissionSignal(
            'PURE1:navigate:proactive_recommendations_dismiss',
            incident.orgId,
        );
        const allowQuoteDownload = this.createAsyncPermissionSignal('PURE1:read:customer_quote', incident.orgId);
        const allowEmailQuote = this.createAsyncPermissionSignal('PURE1:read:customer_quote', incident.orgId);
        const allowPurchaseOrderUpload = this.createAsyncPermissionSignal('PURE1:write:customer_quote', incident.orgId);

        return new ContractRenewalRenderInfo({
            cardPluginComponentName: CONTRACT_RENEWAL_CARD_PLUGIN_NAME,
            dialogPluginComponentName: CONTRACT_RENEWAL_DIALOG_PLUGIN_NAME,
            id: incident.id,
            targetName: earliestExpiringTarget.contractTargetName,
            recommendationHeader: {
                label: 'Contract Renewal',
                emphasis: emphasis,
            },
            subHeaderLabel: this.buildSubHeaderLabel(details.supportContractTargets.length),
            headline: 'Your support contract expires soon',
            detailedDescription:
                'In order to provide the best possible service for your appliances, it is necessary to renew your support contract.',
            daysRemaining: daysRemaining,
            emphasis: emphasis,
            currentBenefitsList: this.getContractBenefitsList(),
            contractImgUrl: this.getContractImageUrl(earliestExpiringTarget.evergreenSubscriptionType),
            allowQuoteDownload: allowQuoteDownload, // Pass the signal
            allowDismiss: allowDismiss, // Pass the signal
            termDetails: this.buildTermDetails(details.subscriptionTermMonths),
            priceTag: this.formatPriceTag(details.customerTotalAmount, details.currency),
            partnerName: details.partnerAccountName,
            allowEmailQuote: allowEmailQuote, // Pass the signal
            allowPurchaseOrderUpload: allowPurchaseOrderUpload, // Pass the signal
            contractTargets: this.buildContractTargetsTableData(details.supportContractTargets),
        });
    }

    /**
     * Finds the support contract target with the earliest expiration date.
     */
    private findEarliestExpiringTarget(targets: any[]): any {
        return targets.reduce((earliest, current) => {
            if (!earliest?.expirationDateUtc) {
                return current;
            }
            if (!current?.expirationDateUtc) {
                return earliest;
            }
            return current.expirationDateUtc.isBefore(earliest.expirationDateUtc) ? current : earliest;
        }, targets[0]);
    }

    /**
     * Calculates days remaining until expiration and validates the contract is not expired.
     * Throws an error if the contract is expired.
     */
    private calculateDaysRemaining(target: any): number {
        const daysRemaining = target.expirationDateUtc.diff(moment.utc(), 'days');
        if (daysRemaining <= 0) {
            throw new Error('QuoteBasedContractRenewal recommendation is expired');
        }
        return daysRemaining;
    }

    /**
     * Builds the sub-header label showing the count of additional arrays.
     * Returns undefined if there's only one array.
     */
    private buildSubHeaderLabel(targetCount: number): string | undefined {
        return targetCount > 1 ? ` + ${targetCount - 1} more` : undefined;
    }

    /**
     * Returns the list of benefits for contract renewal.
     */
    private getContractBenefitsList(): [string, string][] {
        return [
            ['Cost effective entry:', 'Included'],
            ['Lifecycle aligned coverage:', 'Included'],
            ['Schedule maintenance renewals:', 'Included'],
            ['Hardware replacements', 'Basic'],
            ['Streamlined upgrade paths', 'Included'],
        ];
    }

    /**
     * Gets the contract image URL based on subscription type.
     * Validates that the subscription type is supported.
     */
    private getContractImageUrl(subscriptionType: SubscriptionType): string {
        if (subscriptionType !== SubscriptionType.FOREVER && subscriptionType !== SubscriptionType.FOUNDATION) {
            throw new Error(
                `Unsupported evergreen subscription type "${subscriptionType}". ` +
                    `Only FOREVER and FOUNDATION subscription types are supported. Cannot render contract renewal recommendation.`,
            );
        }
        return subscriptionType === SubscriptionType.FOREVER ? 'images/rec-forever.svg' : 'images/rec-foundation.svg';
    }

    /**
     * Builds the term details string if subscription term is available.
     */
    private buildTermDetails(subscriptionTermMonths: number | undefined): string | undefined {
        if (subscriptionTermMonths && subscriptionTermMonths > 0) {
            return `Pricing reflects a ${subscriptionTermMonths}-month(s) term`;
        }
        return undefined;
    }

    /**
     * Formats the price tag with currency symbol.
     */
    private formatPriceTag(amount: number, currency: string): string | null {
        return new CurrencyPipe('en-US').transform(amount, currency, 'symbol', '1.2-2');
    }

    /**
     * Builds the contract targets table data for the dialog.
     * Validates subscription types for all targets.
     */
    private buildContractTargetsTableData(targets: any[]): ContractRenewalTableRow[] {
        return targets.map(target => {
            const daysUntilExpiration = target.expirationDateUtc.diff(moment.utc(), 'days');
            const targetSubscriptionType = target.evergreenSubscriptionType;

            if (
                targetSubscriptionType !== SubscriptionType.FOREVER &&
                targetSubscriptionType !== SubscriptionType.FOUNDATION
            ) {
                throw new Error(
                    `Unsupported evergreen subscription type "${targetSubscriptionType}" for contract target "${target.contractTargetName}". ` +
                        `Only FOREVER and FOUNDATION subscription types are supported. Cannot render contract renewal table row.`,
                );
            }

            return {
                applianceName: target.contractTargetName,
                model: target.model,
                subscriptionType: 'Evergreen' + targetSubscriptionType,
                expiryRemainingDays: `${daysUntilExpiration} day(s)`,
                expirationDateUtc: target.expirationDateUtc,
            };
        });
    }

    /**
     * Creates EoneTierUpgradeRenderInfo from an E//One upgrade incident.
     * Extracts details from the incident and converts to render info.
     *
     * @param incident - The incident with E//One upgrade recommendation code
     * @returns EoneTierUpgradeRenderInfo instance
     */
    private createEoneTierUpgradeRenderInfoFromIncident(incident: Incident): EoneTierUpgradeRenderInfo {
        const details = incident.getEoneUpgradeDetails();

        // Plain tier names (no bandwidth) drive the table columns, the card body
        // and the chart legend. The bandwidth-suffixed variants are used only in
        // the two "Upgrade …" titles (card header and dialog header) so an
        // Adaptive→Adaptive upgrade reads unambiguously there without cluttering
        // the rest of the card.
        // Each license type is resolved once. The bandwidth-suffixed variants
        // append to the already-resolved plain label rather than re-resolving it.
        const currentTierLabel = this.resolveServiceTierDisplayName(details.currentLicenseType);
        const recommendedInfo = getLicenseTypeDisplayInfo(details.recommendedLicenseType);
        const recommendedTierLabel = recommendedInfo.shortenedDisplayName;
        const recommendedLicenseSvg = recommendedInfo.catalogOfferingSvg;

        const currentTierLabelWithBw = this.appendAdaptiveBandwidth(
            currentTierLabel,
            details.currentLicenseType,
            details.currentBwSlaBytesPerSec,
        );
        const recommendedTierLabelWithBw = this.appendAdaptiveBandwidth(
            recommendedTierLabel,
            details.recommendedLicenseType,
            details.recommendedBwSlaBytesPerSec,
        );

        // Create signal for allowDismiss permission
        const allowDismiss = this.createAsyncPermissionSignal(
            'PURE1:navigate:proactive_recommendations_dismiss',
            incident.orgId,
        );
        const allowRequestQuote = this.createAsyncPermissionSignal('PURE1:create:orders', incident.orgId);

        const loadTimeseries: [number, number][] = details.loadTimeseries ?? [];
        const asOfTime = details.asOf ?? 0;

        const hasReserveIncrease = this.detailsHaveReserveIncrease(details);

        return new EoneTierUpgradeRenderInfo({
            cardPluginComponentName: EONE_TIER_UPGRADE_CARD_PLUGIN_NAME,
            dialogPluginComponentName: EONE_TIER_UPGRADE_DIALOG_PLUGIN_NAME,
            id: incident.id,
            targetName: details.entityName,
            recommendationHeader: {
                label: `Upgrade to ${recommendedTierLabelWithBw}`,
                emphasis: 'success',
            },
            currentLicenseType: currentTierLabel,
            recommendedLicenseType: recommendedTierLabel,
            currentLicenseTypeWithBandwidth: currentTierLabelWithBw,
            recommendedLicenseTypeWithBandwidth: recommendedTierLabelWithBw,
            recommendedLicenseSvg,
            reason: incident.reason,
            comparisonRows: this.buildComparisonRows(details),
            chartOptions: buildEoneUpgradeTierChartOptions(
                loadTimeseries,
                details.recommendedProjectedLoadTimeseries,
                `${currentTierLabel} (Current)`,
                `${recommendedTierLabel} (Recommended)`,
                asOfTime,
            ),
            asOfTime,
            recommendedTooltipText:
                details.currentReserveCommitTib &&
                details.recommendedReserveCommitTib &&
                details.currentReserveCommitTib !== details.recommendedReserveCommitTib
                    ? 'This recommendation is based on the recommended reserve commit expansion below.'
                    : undefined,
            hasReserveIncrease,
            allowDismiss: allowDismiss,
            allowRequestQuote,
        });
    }

    /**
     * Builds the comparison table rows from E//One upgrade details.
     * Constructs rows for Max. Supported Load (with Bandwidth/IOPS sub-rows),
     * Projected utilization, and Reserve Commit.
     */
    private buildComparisonRows(details: any): ComparisonRow[] {
        const rows: ComparisonRow[] = [];

        // Max. Supported Load with sub-rows for Bandwidth and IOPS
        const loadSubRows: ComparisonRow[] = [];
        if (
            details.currentBwSlaBytesPerSec &&
            details.recommendedBwSlaBytesPerSec &&
            details.recommendedBwSlaBytesPerSec > details.currentBwSlaBytesPerSec
        ) {
            loadSubRows.push({
                label: '',
                currentValue: { text: `Bandwidth: ${this.formatBandwidth(details.currentBwSlaBytesPerSec, 2)}` },
                recommendedValue: {
                    text: `Bandwidth: ${this.formatBandwidth(details.recommendedBwSlaBytesPerSec, 2)}`,
                },
            });
        }
        if (
            details.currentIopsSla &&
            details.recommendedIopsSla &&
            details.recommendedIopsSla > details.currentIopsSla
        ) {
            loadSubRows.push({
                label: '',
                currentValue: { text: `IOPS: ${formatIOPsStr(details.currentIopsSla, 0)}` },
                recommendedValue: { text: `IOPS: ${formatIOPsStr(details.recommendedIopsSla, 0)}` },
            });
        }
        if (loadSubRows.length > 0) {
            rows.push({ label: 'Max. Supported Load', subRows: loadSubRows });
        }

        // Projected utilization — only show if both exist and recommended is lower (better)
        if (
            details.projectedLoadCurrentTierPercent &&
            details.projectedLoadRecommendedTierPercent &&
            details.projectedLoadRecommendedTierPercent < details.projectedLoadCurrentTierPercent
        ) {
            const label = details.projectedLoadDays
                ? `Projected utilization in ${details.projectedLoadDays} days`
                : 'Projected utilization';
            const isOverLimit = details.projectedLoadCurrentTierPercent > 100;
            rows.push({
                label,
                currentValue: { text: `${details.projectedLoadCurrentTierPercent} %`, isWarning: isOverLimit },
                recommendedValue: {
                    text: `${details.projectedLoadRecommendedTierPercent}%`,
                    // Utilization falls with the upgrade (this row only renders when
                    // recommended < current), so the trend arrow points down — while
                    // keeping the green "improved" colour, since a lower load is good.
                    arrowDirection: 'down',
                },
            });
        }

        // Reserve Commit — only show if both exist and recommended is greater
        if (this.detailsHaveReserveIncrease(details)) {
            rows.push({
                label: 'Reserve Commit',
                currentValue: { text: `${details.currentReserveCommitTib} TiB` },
                recommendedValue: {
                    text: `${details.recommendedReserveCommitTib} TiB`,
                },
            });
        }

        return rows;
    }

    /**
     * Single source of truth for "does this incident recommend a reserve commit
     * increase?". Drives both the Reserve Commit table row and the dialog's
     * projected-load legend caption — keeping them in lockstep.
     */
    private detailsHaveReserveIncrease(details: any): boolean {
        return (
            !!details.currentReserveCommitTib &&
            !!details.recommendedReserveCommitTib &&
            details.recommendedReserveCommitTib > details.currentReserveCommitTib
        );
    }

    /**
     * Formats a bandwidth SLA (bytes/sec) with SI units. Shared by the Max.
     * Supported Load row (2 decimals, e.g. "7.20 GB/s") and the Adaptive
     * tier-name suffix (0 decimals, e.g. "5 GB/s" — reads as a name rather than
     * a precise measurement).
     */
    private formatBandwidth(bytesPerSec: number, decimals: number): string {
        const { value, unit } = formatNetworkSpeedSI(bytesPerSec, decimals);
        return `${value} ${unit}`.trim();
    }

    /**
     * Creates EoneCapacityExpansionRenderInfo from an E//One commit-expansion incident.
     */
    private createEoneCapacityExpansionRenderInfoFromIncident(incident: Incident): EoneCapacityExpansionRenderInfo {
        const details = incident.getEoneUpgradeDetails();

        const serviceTier = details.currentLicenseType; // we show the entire service tier name not the shortened version
        const allowDismiss = this.createAsyncPermissionSignal(
            'PURE1:navigate:proactive_recommendations_dismiss',
            incident.orgId,
        );
        const allowRequestQuote = this.createAsyncPermissionSignal('PURE1:create:orders', incident.orgId);

        const capacityTimeseries = details.capacityTimeseriesTib;
        const asOfTime = details.asOf;
        const currentReserveTib = details.currentReserveCommitTib;
        const recommendedReserveTib = details.recommendedReserveCommitTib;

        if (!capacityTimeseries?.length || !asOfTime || !currentReserveTib || !recommendedReserveTib) {
            throw new Error(
                `EoneCapacityExpansion incident ${incident.id} is missing required fields: ` +
                    `capacityTimeseries=${JSON.stringify(capacityTimeseries)}, asOfTime=${asOfTime}, ` +
                    `currentReserveTib=${currentReserveTib}, recommendedReserveTib=${recommendedReserveTib}`,
            );
        }

        // Current usage = capacity-timeseries point closest to (and not before) as-of time.
        // Mirrors the legacy getCurrentUsagePercent behaviour.
        const currentTibPoint = capacityTimeseries.find(([ts]) => ts > asOfTime - 1);
        const currentTibValue = currentTibPoint?.[1] ?? 0;
        const currentUsagePercent = Math.round((currentTibValue / currentReserveTib) * 100);
        const recommendedUsagePercent = Math.round((currentTibValue / recommendedReserveTib) * 100);

        // Projected % = last point of the capacity timeseries as a percentage of current reserve.
        const lastTibValue = capacityTimeseries[capacityTimeseries.length - 1][1];
        const projectedUsagePercent = Math.round((lastTibValue / currentReserveTib) * 100);

        const formatDays = new FormatFullInDaysPipe();
        const daysToExceedCurrent = details.daysToExceedCurrentReserve ?? 0;
        const daysToExceedRecommended = details.daysToExceedProjectedReserve;
        const forecastDays = details.thresholdHorizonDays ?? 0;
        // Sign-prefixed delta (e.g. "+98" or "-58"). JS prints negatives with
        // a leading "-" naturally; we only need to add "+" for non-negatives.
        const formatDelta = (n: number): string => (n >= 0 ? `+${n}` : `${n}`);
        const projectedTimeToOnDemandLabel = formatDays.transform(daysToExceedCurrent) ?? '';
        // Recommended cell: blank if the recommended reserve isn't crossed within
        // the forecast window; otherwise show the recommended days. `isImproved`
        // alone drives whether the dialog renders the upgrade styling.
        const projectedTimeToOnDemandRecommended =
            daysToExceedRecommended == null || daysToExceedRecommended >= forecastDays
                ? { text: '' }
                : {
                      text: formatDays.transform(daysToExceedRecommended) ?? '',
                      isImproved: daysToExceedRecommended > daysToExceedCurrent,
                  };

        return new EoneCapacityExpansionRenderInfo({
            cardPluginComponentName: EONE_CAPACITY_EXPANSION_CARD_PLUGIN_NAME,
            dialogPluginComponentName: EONE_CAPACITY_EXPANSION_DIALOG_PLUGIN_NAME,
            id: incident.id,
            targetName: details.entityName,
            recommendationHeader: {
                label: 'Expand Reserved Capacity',
                emphasis: 'success',
            },
            serviceTier,
            currentReserveTib,
            recommendedReserveTib,
            comparisonRows: [
                {
                    label: 'Reserved',
                    currentValue: { text: `${currentReserveTib} TiB` },
                    recommendedValue: {
                        text: `${recommendedReserveTib} TiB (${formatDelta(recommendedReserveTib - currentReserveTib)})`,
                    },
                },
                {
                    label: 'Current usage',
                    currentValue: { text: `${currentUsagePercent}%`, isWarning: true },
                    recommendedValue: {
                        text: `${recommendedUsagePercent}% (${formatDelta(recommendedUsagePercent - currentUsagePercent)})`,
                        // A larger reserve lowers usage %, so the trend arrow points
                        // down while staying green — a lower usage is the improvement.
                        arrowDirection: 'down',
                    },
                },
                {
                    label: 'Projected time to on-demand',
                    currentValue: { text: projectedTimeToOnDemandLabel },
                    recommendedValue: projectedTimeToOnDemandRecommended,
                },
            ],
            projectedUsagePercent,
            thresholdHorizonDays: details.thresholdHorizonDays ?? 0,
            daysToExceedCurrentReserve: details.daysToExceedCurrentReserve ?? 0,
            chartOptions: buildEoneCapacityExpansionChartOptions(
                capacityTimeseries,
                currentReserveTib,
                recommendedReserveTib,
                asOfTime,
            ),
            sparklineOptions: buildEoneCapacityExpansionSparklineOptions(
                details.sparklineTimeseries ?? [],
                asOfTime,
                currentUsagePercent,
                projectedUsagePercent,
                details.thresholdHorizonDays ?? 0,
            ),
            asOfTime,
            allowDismiss,
            allowRequestQuote,
        });
    }

    /**
     * Resolves an E//One license type to its shortened display name.
     */
    private resolveServiceTierDisplayName(licenseType: string): string {
        return getLicenseTypeDisplayInfo(licenseType).shortenedDisplayName;
    }

    /**
     * Appends the bandwidth SLA to an already-resolved tier display name, but
     * only for Adaptive tiers — whose name alone ("UBF Adaptive") does not convey
     * the purchased bandwidth. This disambiguates an Adaptive→Adaptive upgrade in
     * the card/dialog titles (e.g. "UBF Adaptive 5 GB/s"). Fixed tiers
     * (Performance/Premium/…) and a missing/zero SLA return the name unchanged.
     */
    private appendAdaptiveBandwidth(displayName: string, licenseType: string, bwSlaBytesPerSec?: number): string {
        if (isAdaptiveLicenseType(licenseType) && bwSlaBytesPerSec) {
            return `${displayName} ${this.formatBandwidth(bwSlaBytesPerSec, 0)}`;
        }
        return displayName;
    }

    /**
     * Creates NvmeNowRenderInfo from a prorec-nvme-now incident. The awareness-only
     * payload has no recommended model / target capacity / timeseries; the RenderInfo
     * carries the current-side fields plus a rolled-up list of affected components.
     */
    private createNvmeNowRenderInfoFromIncident(incident: Incident): NvmeNowRenderInfo {
        const details = incident.getNvmeNowDetails();

        const allowDismiss = this.createAsyncPermissionSignal(
            'PURE1:navigate:proactive_recommendations_dismiss',
            incident.orgId,
        );
        const allowContactAccountTeam = this.createAsyncPermissionSignal('PURE1:create:orders', incident.orgId);

        const detailRows: NvmeNowDetailRow[] = [
            { label: 'Current model', values: [details.currentModel] },
            {
                label: 'Current usable (TiB)',
                values: [
                    RecommendationProcessingService.decimalPipe.transform(details.currentUsableTiB, '1.1-1') ?? '',
                ],
            },
        ];
        if (details.purityVersion) {
            detailRows.push({ label: 'Purity version', values: [details.purityVersion] });
        }
        detailRows.push({
            label: 'Affected components',
            values: RecommendationProcessingService.buildAffectedComponentsValues(details.eolComponents),
        });
        if (details.earliestEolDate) {
            detailRows.push({
                label: 'Component end-of-life date',
                values: [details.earliestEolDate.format('MMM YYYY')],
            });
        }

        return new NvmeNowRenderInfo({
            cardPluginComponentName: NVME_NOW_CARD_PLUGIN_NAME,
            dialogPluginComponentName: NVME_NOW_DIALOG_PLUGIN_NAME,
            id: incident.id,
            targetName: details.entityName,
            recommendationHeader: {
                label: 'NVMe Now',
                emphasis: 'warning',
            },
            detailRows,
            allowDismiss,
            allowContactAccountTeam,
        });
    }

    /**
     * Creates WorkflowRecipeRenderInfo from a `prorec-eone-workflow-recipe` incident.
     *
     * The payload groups several recipes under one signal, so this produces ONE
     * RenderInfo (one card) carrying the whole recipe list — not one per recipe.
     * Recipes are passed through raw; the card and dialog plugins own how a recipe
     * with a missing title, description or connector list is displayed.
     */
    private createWorkflowRecipeRenderInfoFromIncident(incident: Incident): WorkflowRecipeRenderInfo {
        const details = incident.getWorkflowRecipeDetails();

        const allowDismiss = this.createAsyncPermissionSignal(
            'PURE1:navigate:proactive_recommendations_dismiss',
            incident.orgId,
        );

        return new WorkflowRecipeRenderInfo({
            cardPluginComponentName: WORKFLOW_RECIPE_CARD_PLUGIN_NAME,
            dialogPluginComponentName: WORKFLOW_RECIPE_DIALOG_PLUGIN_NAME,
            id: incident.id,
            // Fixed dark line per the Figma; the green line below it is the shared
            // metadata display name, same as every other card.
            targetName: WORKFLOW_RECIPE_CARD_TARGET_NAME,
            recommendationHeader: {
                label: getRecommendationDisplayName(RecommendationCode.WorkflowRecipe),
                emphasis: 'success',
            },
            signal: details.signal,
            // Signal-derived title, e.g. "Recommended Recipe for Evergreen//One". No
            // longer fits the card header, so the dialog shows it instead.
            signalTitle: details.entityName,
            recipes: details.recipes,
            allowDismiss,
        });
    }

    /**
     * Creates FbNorwayEolRenderInfo from a prorec-eol-fb-norway incident. Awareness-only:
     * there is no recommended model, so it carries just the current model and, when known,
     * the end-of-support date. The dialog shows both; the card shows the day count to that
     * date instead.
     */
    private createFbNorwayEolRenderInfo(incident: Incident): FbNorwayEolRenderInfo {
        const details = incident.getFbNorwayEolDetails();

        const allowDismiss = this.createAsyncPermissionSignal(
            'PURE1:navigate:proactive_recommendations_dismiss',
            incident.orgId,
        );
        const allowContactAccountTeam = this.createAsyncPermissionSignal('PURE1:create:orders', incident.orgId);

        return new FbNorwayEolRenderInfo({
            cardPluginComponentName: FB_NORWAY_EOL_CARD_PLUGIN_NAME,
            dialogPluginComponentName: FB_NORWAY_EOL_DIALOG_PLUGIN_NAME,
            id: incident.id,
            targetName: details.entityName,
            recommendationHeader: { label: 'End of Life', emphasis: 'warning' },
            currentModel: details.currentModel,
            eolDate: details.eolDate?.toDate() ?? null,
            allowDismiss,
            allowContactAccountTeam,
        });
    }

    /** Creates EoscFbRenderInfo from a prorec-end-of-support-contract-fb incident. */
    private createEoscFbRenderInfo(incident: Incident): EoscFbRenderInfo {
        const allowDismiss = this.createAsyncPermissionSignal(
            'PURE1:navigate:proactive_recommendations_dismiss',
            incident.orgId,
        );

        return new EoscFbRenderInfo({
            cardPluginComponentName: EOSC_FB_CARD_PLUGIN_NAME,
            dialogPluginComponentName: EOSC_FB_DIALOG_PLUGIN_NAME,
            id: incident.id,
            targetName: incident.getEntityName(),
            recommendationHeader: {
                label: getRecommendationDisplayName(RecommendationCode.EoSCFB),
                emphasis: 'danger',
            },
            eosc: this.createEoscRenewal(incident),
            allowDismiss,
        });
    }

    /** The renewal of an end-of-support-contract recommendation. */
    private createEoscRenewal(eoscRecommendation: Incident): EoscRenewal {
        // The same two unscoped checks the legacy hardware dialog's Renew button makes.
        const allowOrders = this.createUnscopedPermissionSignal('PURE1:create:orders');
        const allowArrayMetadata = this.createUnscopedPermissionSignal('PURE1:invoke:array_metadata');
        return createEoscRenewal(
            eoscRecommendation.id,
            computed(() => allowOrders() && allowArrayMetadata()),
        );
    }

    /**
     * Buckets eol_components by componentModel and produces one "N× <label>"
     * line per group. Falls back to "Hardware Component" when a component's
     * model is missing so partial payloads still show a useful label. When
     * the list is empty, returns a single explanatory line so the dialog
     * still has something to render in the Affected components row.
     */
    private static buildAffectedComponentsValues(components: NvmeNowEolComponent[] = []): string[] {
        const counts = new Map<string, number>();
        for (const c of components) {
            const label = c?.componentModel?.trim() || 'Hardware Component';
            counts.set(label, (counts.get(label) ?? 0) + 1);
        }
        if (counts.size === 0) {
            return ['No end-of-life components detected'];
        }
        return Array.from(counts.entries()).map(([label, count]) => `${count}× ${label}`);
    }

    private static readonly decimalPipe = new DecimalPipe('en-US');
}
